import React from "react";
import { Compass, Sparkles, ShieldCheck } from "lucide-react";
import { calcHealthRadarDimensions } from "./helpers";
import WidgetInfoTooltip from "./WidgetInfoTooltip";

const RADAR_PILLAR_DETAILS = {
  cardio: {
    title: "Pilar Cardiovascular",
    what: "Porcentaje de colaboradores con cifras de presión arterial óptimas en reposo.",
    calculation: "PAS < 130 mmHg y PAD < 85 mmHg.",
    standard: "ACC/AHA 2017 & ESC/ESH 2018 Guidelines.",
    relevance: "Protección contra eventos coronarios, crisis hipertensivas y ausentismo.",
  },
  metabolic: {
    title: "Pilar Metabolismo & Peso",
    what: "Porcentaje de colaboradores con índice cintura/talla saludable (sin grasa visceral patológica).",
    calculation: "Índice Cintura/Talla (WHtR) ≤ 0.50.",
    standard: "IDF & Ashwell et al. 2012 Consensus.",
    relevance: "Prevención de resistencia insulínica, diabetes tipo 2 y síndrome metabólico.",
  },
  activity: {
    title: "Pilar Actividad Ocupacional",
    what: "Porcentaje de colaboradores que alcanzan niveles moderados o altos de movimiento físico.",
    calculation: "Categoría IPAQ Moderado/Vigoroso (≥ 600 MET-minutos/semana).",
    standard: "OMS Directrices Mundiales 2020 & Cuestionario IPAQ.",
    relevance: "Disminución del riesgo de trastornos musculoesqueléticos y sedentarismo laboral.",
  },
  sleep: {
    title: "Pilar Recuperación & Sueño",
    what: "Porcentaje de colaboradores con descanso nocturno fisiológicamente reparador.",
    calculation: "Duración de sueño nocturno continuado ≥ 7.0 horas.",
    standard: "National Sleep Foundation (NSF) & AASM Guidelines.",
    relevance: "Atención ejecutiva diurna, menor tiempo de reacción y prevención de accidentes.",
  },
  emotional: {
    title: "Pilar Equilibrio Emocional",
    what: "Porcentaje de la plantilla con tono neurovegetativo y niveles de estrés bajo control.",
    calculation: "Índice de Tensión de Baevsky ≤ 350 pts (telemetría rPPG / HRV).",
    standard: "Baevsky RM & Task Force of ESC/NASPE 1996.",
    relevance: "Prevención de desgaste ocupacional crónico, presentismo y síndrome de burnout.",
  },
  respiratory: {
    title: "Pilar Eficiencia Pulmonar",
    what: "Porcentaje de colaboradores con saturación periférica de oxígeno normal en reposo.",
    calculation: "SpO2 transcutánea o rPPG ≥ 95%.",
    standard: "Consenso OMS / American Thoracic Society (ATS).",
    relevance: "Oxigenación tisular celular óptima y descarte de hipoxemia subclínica.",
  },
};

export default function HealthRadarHexagon({ employees = [], forceSimulated = false }) {
  const radar = calcHealthRadarDimensions(employees, forceSimulated);
  const { dimensions, avgScore, benchmarkAvg } = radar;

  // Parámetros geométricos del hexágono en SVG
  const width = 360;
  const height = 300;
  const cx = 180;
  const cy = 150;
  const maxRadius = 100;
  const numAxes = dimensions.length; // 6

  // Coordenadas para un valor porcentual (0-100) en el eje i
  const getCoordinates = (index, value) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / numAxes;
    const r = (value / 100) * maxRadius;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    return { x, y, angle };
  };

  // Puntos del polígono de la empresa
  const companyPolygonPoints = dimensions
    .map((dim, idx) => {
      const { x, y } = getCoordinates(idx, dim.score);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // Puntos del polígono de benchmark (referencia ideal)
  const benchmarkPolygonPoints = dimensions
    .map((dim, idx) => {
      const { x, y } = getCoordinates(idx, dim.benchmark);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // Anillos concéntricos (25%, 50%, 75%, 100%)
  const rings = [25, 50, 75, 100];

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm relative"
      data-testid="health-radar-hexagon"
    >
      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
              <Compass size={12} />
              Evaluación Holística 360°
            </span>
            <span className="text-xs text-slate-400">
              6 pilares de bienestar evaluados
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <h3 className="text-base font-bold text-slate-800">
              Radar Hexagonal de Salud Integral Corporativa
            </h3>
            <WidgetInfoTooltip
              title="Radar Hexagonal de Salud Integral 360°"
              align="left"
              what="Perfil poligonal multifactorial que sintetiza el rendimiento global de la fuerza laboral a lo largo de 6 dimensiones fisiológicas y conductuales."
              calculation="Normalización de 0 a 100 de cada dimensión: Composición Corporal, Control Hemodinámico, Resiliencia al Estrés, Higiene del Sueño, Actividad Física y Eficiencia Pulmonar. Se contrasta contra la meta estándar corporativa (80 pts)."
              standard="ACC/AHA 2017 • IDF/Ashwell 2012 • OMS IPAQ 2020 • NSF 2015 • Baevsky RM (Task Force ESC/NASPE) • ATS SpO2."
              relevance="Permite a la Alta Dirección y comités de bienestar visualizar desbalances de un vistazo (ej. excelente actividad física pero mala recuperación de sueño o estrés crítico) para balancear la inversión en beneficios de salud."
            />
          </div>
          <p className="text-xs text-slate-500">
            Desempeño holístico de la plantilla contrastado contra el estándar óptimo corporativo.
          </p>
        </div>

        {/* Score de Desempeño Holístico */}
        <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/60 rounded-xl px-3.5 py-2">
          <div className="text-right">
            <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
              Índice Holístico
            </p>
            <p className="text-lg font-extrabold text-teal-700 leading-tight">
              {avgScore} <span className="text-xs text-slate-400 font-normal">/ 100</span>
            </p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold">
            <Sparkles size={18} />
          </div>
        </div>
      </div>

      {/* Contenedor Flex: Radar SVG a la izquierda + Desglose a la derecha */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        {/* SVG Radar (7 cols) */}
        <div className="lg:col-span-7 flex flex-col items-center justify-center relative">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full max-w-[380px] h-auto select-none"
            style={{ fontFamily: "Inter, system-ui, sans-serif" }}
          >
            {/* Anillos concéntricos */}
            {rings.map((pct) => {
              const ringPoints = Array.from({ length: numAxes })
                .map((_, idx) => {
                  const { x, y } = getCoordinates(idx, pct);
                  return `${x.toFixed(1)},${y.toFixed(1)}`;
                })
                .join(" ");

              return (
                <polygon
                  key={`ring-${pct}`}
                  points={ringPoints}
                  fill={pct === 100 ? "#F8FAFC" : "none"}
                  stroke="#E2E8F0"
                  strokeWidth={pct === 100 ? 1.5 : 1}
                  strokeDasharray={pct === 100 ? "none" : "2 2"}
                />
              );
            })}

            {/* Ejes radiales (rayos desde el centro hacia cada vértice) */}
            {dimensions.map((_, idx) => {
              const { x, y } = getCoordinates(idx, 100);
              return (
                <line
                  key={`axis-${idx}`}
                  x1={cx}
                  y1={cy}
                  x2={x}
                  y2={y}
                  stroke="#CBD5E1"
                  strokeWidth={1}
                />
              );
            })}

            {/* Polígono de Benchmark (Estándar Empresa Óptima) */}
            <polygon
              points={benchmarkPolygonPoints}
              fill="rgba(16, 185, 129, 0.06)"
              stroke="#10B981"
              strokeWidth={1.8}
              strokeDasharray="4 4"
            />

            {/* Polígono de la Empresa Actual */}
            <polygon
              points={companyPolygonPoints}
              fill="rgba(13, 148, 136, 0.28)"
              stroke="#0D9488"
              strokeWidth={2.4}
            />

            {/* Puntos en los vértices de la empresa */}
            {dimensions.map((dim, idx) => {
              const { x, y } = getCoordinates(idx, dim.score);
              return (
                <circle
                  key={`point-${idx}`}
                  cx={x}
                  cy={y}
                  r={4.5}
                  fill="#0D9488"
                  stroke="#FFFFFF"
                  strokeWidth={1.8}
                />
              );
            })}

            {/* Etiquetas de los vértices (usando dangerouslySetInnerHTML para blindaje Babel) */}
            {dimensions.map((dim, idx) => {
              const { x, y, angle } = getCoordinates(idx, 118);
              let textAnchor = "middle";
              if (Math.cos(angle) > 0.3) textAnchor = "start";
              else if (Math.cos(angle) < -0.3) textAnchor = "end";

              return (
                <text
                  key={`label-${idx}`}
                  x={x}
                  y={y + 4}
                  textAnchor={textAnchor}
                  fontSize="9.5"
                  fontWeight="600"
                  fill="#475569"
                  dangerouslySetInnerHTML={{ __html: dim.label }}
                />
              );
            })}
          </svg>

          {/* Leyenda interactiva bajo el radar */}
          <div className="flex items-center gap-4 text-xs mt-2">
            <span className="flex items-center gap-1.5 text-slate-700 font-medium">
              <span className="w-3 h-3 rounded-full bg-teal-600 inline-block" />
              Plantilla Actual ({avgScore} pts)
            </span>
            <span className="flex items-center gap-1.5 text-slate-500">
              <span className="w-3 h-0.5 border-t-2 border-dashed border-emerald-500 inline-block" />
              Estándar Óptimo ({benchmarkAvg} pts)
            </span>
          </div>
        </div>

        {/* Desglose de los 6 Pilares (5 cols) */}
        <div className="lg:col-span-5 space-y-2.5">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
            Desglose de los 6 Pilares de Bienestar
          </h4>
          {dimensions.map((dim) => {
            const details = RADAR_PILLAR_DETAILS[dim.key];
            return (
              <div
                key={dim.key}
                className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 flex flex-col justify-between"
              >
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-700">
                      {dim.label}
                    </span>
                    {details && (
                      <WidgetInfoTooltip
                        title={details.title}
                        size="sm"
                        align="left"
                        what={details.what}
                        calculation={details.calculation}
                        standard={details.standard}
                        relevance={details.relevance}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-extrabold text-slate-900">
                      {dim.score}
                    </span>
                    <span className="text-[10px] text-slate-400 font-normal">
                      / meta {dim.benchmark}
                    </span>
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, dim.score)}%`,
                      backgroundColor: dim.score >= dim.benchmark ? "#10B981" : "#0D9488",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
